import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Avatar, Tag, Table, Typography } from "antd";
import { doGet } from "../../../../API/apis";
import { useStateGlobalContext } from "../../../../contexts/GlobalContext";
import { useOutletContext } from "react-router-dom";
import StaffRecord from "../../../../components/StaffRecord/StaffRecord";
import Notification from "../../../../components/Notification/Notification";

const { Text } = Typography;

const ClinicStaff = () => {
  const currentDate = new Date();
  const [tableLoading, setTableLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [staffAvailability, setStaffAvailability] = useState(null);
  const { getUserInfo } = useStateGlobalContext();
  const [showStaffDetails, setShowStaffDetails] = useState({
    show: false,
    data: null
  });
  const [officeDateFormat, officeTimeFormat, officeTimezone] = useOutletContext();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const currentDayOptions = useMemo(() => ({ 
    weekday: 'long', 
    timeZone: officeTimezone || "US/Central" 
  }), [officeTimezone]);
  
  const currentDay = useMemo(() => 
    currentDate?.toLocaleString('en-US', currentDayOptions), 
    [currentDate, currentDayOptions]
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getRoleDisplay = useCallback((role) => {
    const roleMap = {
      doctor: "Provider",
      clinic: "Owner", 
      manager: "Admin"
    };
    return roleMap[role] || "Nurse";
  }, []);

  const getAvailabilityStatus = useCallback((record) => {
    const schedule = record?.availability?.schedule;
    if (!schedule) return { status: false, timeText: "No Time" };
    
    const hasTime = schedule.start_time && schedule.end_time;
    return {
      status: schedule.status,
      timeText: hasTime ? `${schedule.start_time} - ${schedule.end_time}` : "No Time"
    };
  }, []);

  const columns = useMemo(() => [
    {
      key: 1,
      title: "Active Staff",
      width: windowWidth < 576 ? "60%" : "40%",
      dataIndex: "name",
      fixed: 'left',
      render: (text, record) => (
        <div className="flex items-center gap-2 sm:gap-4">
          <Avatar
            shape="circle"
            style={{ backgroundColor: "#f56a00" }}
          >
            {text?.charAt(0)?.toUpperCase() || "?"}
          </Avatar>
          <div>
            <p className="font-semibold text-gray-800 text-xs sm:text-sm">
              {text || "Unknown"}
            </p>
            <p className="text-xs text-gray-500">
              {getRoleDisplay(record?.availability?.schedule?.role)}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 2,
      title: "Today's Availability",
      align: "center",
      width: windowWidth < 576 ? "40%" : "30%",
      render: (_, record) => {
        const { status, timeText } = getAvailabilityStatus(record);
        return (
          <div className="flex flex-col items-center">
            <Tag
              className="rounded-lg mb-1 text-xs"
              color={status ? "#87d068" : "#f87171"}
            >
              {status ? "Available" : "N/A"}
            </Tag>
            <Text className="text-xs text-gray-600">
              {timeText}
            </Text>
          </div>
        );
      },
    },
    {
      key: 4,
      title: "Notes",
      width: "30%",
      align: "center",
      ellipsis: true,
      responsive: ['md'],
      render: (_, record) => (
        <Text className="text-gray-600 text-xs sm:text-sm" ellipsis>
          {record?.availability?.schedule?.note || "No Notes"}
        </Text>
      ),
    },
  ], [windowWidth, getRoleDisplay, getAvailabilityStatus]);

  const getStaffAvailability = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await doGet("/settings/staff-availability/");
      if (response?.status === 200) {
        setStaffAvailability(response.data);
      } else {
        throw new Error("Failed to fetch staff availability");
      }
    } catch (error) {
      console.error("Error fetching staff availability:", error);
      Notification.error("Failed to load staff schedule");
    } finally {
      setTableLoading(false);
    }
  }, []);

  const showStaffInfo = useCallback(async (scheduleId) => {
    if (!scheduleId) {
      Notification.error("Invalid staff record");
      return;
    }

    setModalLoading(true);
    setShowStaffDetails({
      show: true,
      data: null
    });

    try {
      const response = await doGet(`/settings/settings-detail/${scheduleId}/?q=False`);
      if (response?.status === 200) {
        setShowStaffDetails({
          show: true,
          data: response.data,
        });
      } else {
        throw new Error("Failed to fetch staff details");
      }
    } catch (error) {
      console.error("Error fetching staff details:", error);
      Notification.error("Failed to load staff details");
      setShowStaffDetails({
        show: false,
        data: null
      });
    } finally {
      setModalLoading(false);
    }
  }, []);
  
  const hideStaffInfo = useCallback(() => {
    setShowStaffDetails({
      show: false,
      data: null
    });
  }, []);

  useEffect(() => {
    // Call functions only once on mount
    const initializeData = async () => {
      await getStaffAvailability();
      getUserInfo();
    };
    
    initializeData();
  }, []); // Empty dependency array - only run once on mount

  return (
    <>
      <Card className="h-full shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
          <h1 className="font-bold text-lg">Staff Schedule</h1>
          <h3 className="text-xs sm:text-sm text-gray-600">
            Today is {currentDay}
          </h3>
        </div>
        <div className="overflow-x-auto w-full">
          <Table
            size="small"
            loading={tableLoading}
            scroll={{ x: '500px', y: 220 }}
            columns={columns}
            dataSource={staffAvailability?.staff || []}
            pagination={false}
            onRow={(record) => ({
              onClick: () => showStaffInfo(record?.availability?.schedule?.id),
            })}
            rowKey="id"
            className="cursor-pointer min-w-full"
          />
        </div>
      </Card>

      <StaffRecord 
        show={showStaffDetails?.show} 
        onClose={hideStaffInfo} 
        data={showStaffDetails?.data} 
        loading={modalLoading}
      />
    </>
  );
};

export default ClinicStaff;
